import { useState, useRef, useEffect } from "react";
import { useDataProvider, useNotify } from "ra-core";
import { Pencil, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export interface InlineEditFieldProps {
  label: string;
  value?: string | number | null;
  displayValue?: React.ReactNode;
  resource: string;
  recordId: string | number;
  field: string;
  previousData?: Record<string, unknown>;
  type?:
    | "text"
    | "number"
    | "select"
    | "email"
    | "tel"
    | "textarea"
    | "date"
    | "currency";
  choices?: { value: string | number; label: string }[];
  className?: string;
  onSaved?: (newValue: string | number) => void;
  customSave?: (newValue: string | number) => Promise<void>;
  emptyText?: string;
  readOnly?: boolean;
}

export const InlineEditField = ({
  label,
  value,
  displayValue,
  resource,
  recordId,
  field,
  previousData = {},
  type = "text",
  choices = [],
  className,
  onSaved,
  customSave,
  emptyText = "Não informado",
  readOnly = false,
}: InlineEditFieldProps) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [editing, setEditing] = useState(false);
  const [localValue, setLocalValue] = useState<string | number>(value ?? "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync when parent value changes
  useEffect(() => {
    if (!editing) setLocalValue(value ?? "");
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      if (type === "textarea" && textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.select();
      } else if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }
  }, [editing, type]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (customSave) {
        await customSave(localValue);
      } else {
        await dataProvider.update(resource, {
          id: recordId,
          data: { [field]: localValue },
          previousData,
        });
      }
      onSaved?.(localValue);
      setEditing(false);
      notify("Atualizado com sucesso", { type: "success" });
    } catch {
      notify("Erro ao salvar", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setLocalValue(value ?? "");
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (type !== "textarea" && e.key === "Enter") handleSave();
    if (e.key === "Escape") handleCancel();
  };

  const selectedChoiceLabel =
    type === "select"
      ? choices.find(
          (c: any) =>
            String(c.value) === String(value) ||
            (c.id && String(c.id) === String(value)) ||
            (c.slug && String(c.slug) === String(value)),
        )?.label
      : undefined;

  const renderContent = () => {
    if (displayValue !== undefined && displayValue !== null) {
      return displayValue;
    }
    if (type === "select") {
      return selectedChoiceLabel || emptyText;
    }
    return value ? String(value) : emptyText;
  };

  return (
    <div
      className={cn(
        "group flex flex-col gap-1 py-2 border-b border-border/40 last:border-b-0",
        className,
      )}
    >
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
        {label}
      </span>

      {editing ? (
        <div className="flex items-center gap-2 mt-0.5">
          {type === "select" ? (
            <Select
              value={String(localValue)}
              onValueChange={(v) => {
                setLocalValue(v);
              }}
            >
              <SelectTrigger className="h-9 text-sm flex-1">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                {choices.map((c) => {
                  const val = String(c.value ?? "");
                  if (!val) return null;
                  return (
                    <SelectItem key={val} value={val}>
                      {c.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          ) : type === "textarea" ? (
            <Textarea
              ref={textareaRef}
              value={String(localValue)}
              onChange={(e) => setLocalValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="text-sm flex-1 min-h-[70px]"
              disabled={saving}
            />
          ) : (
            <Input
              ref={inputRef}
              type={type}
              value={localValue}
              onChange={(e) =>
                setLocalValue(
                  type === "number" ? Number(e.target.value) : e.target.value,
                )
              }
              onKeyDown={handleKeyDown}
              className="h-9 text-sm flex-1"
              disabled={saving}
            />
          )}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="p-2 rounded-md bg-emerald-600/20 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-colors cursor-pointer"
              title="Salvar"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="p-2 rounded-md bg-muted text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              title="Cancelar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 min-h-[28px]">
          <div
            onClick={() => !readOnly && setEditing(true)}
            className={cn(
              "text-sm font-medium text-foreground truncate cursor-pointer hover:text-primary transition-colors flex-1",
              !value && "text-muted-foreground/70 italic font-normal text-xs",
            )}
            title={!readOnly ? "Clique para editar" : undefined}
          >
            {renderContent()}
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="p-1.5 rounded-md text-muted-foreground/50 opacity-0 group-hover:opacity-100 hover:text-primary hover:bg-muted transition-all flex-shrink-0 cursor-pointer"
              title={`Editar ${label}`}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
